import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InventorySKUListing } from '@views/StockBySKU/components/types';
import {
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/20/solid';
import { CubeIcon } from '@heroicons/react/24/outline';
import { Button } from '@tailwindComponents/Button';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { ProductTableRow } from './ProductTableRow';
import { PanelSaveStrip } from './PanelSaveStrip';
import { ProductGroupListing, productGroupKey } from './productGroups';
import {
  ActivePanelEdit,
  PalletPanelDraft,
  PanelEditTarget,
  ProductPanelDraft,
  SkuPanelDraft,
  emptyPalletDraft,
  isActiveEditSaveable,
  palletDraftOf,
  productDraftOf,
  skuDraftOf,
  withAnEmptyAdditionalField,
} from './panelEdit';

export interface ProductTableProps {
  groups: ProductGroupListing[];
  isLoading: boolean;
  isWarehouseUser: boolean;
  onArchiveSku: (sku: InventorySKUListing) => void;
  onArchiveProduct: (group: ProductGroupListing) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  canManageSkus: boolean;
  onUploadClick: () => void;
  onAddClick: () => void;
  onAddPackSize: (group: ProductGroupListing) => void;
  onCloneProduct: (group: ProductGroupListing) => void;
  onCloneProductSkus: (group: ProductGroupListing) => void;
  onCloneHierarchy: (group: ProductGroupListing) => void;
  onSavePanelEdit: (edit: ActivePanelEdit) => Promise<boolean>;
  onRemovePallet: (
    sku: InventorySKUListing,
    palletIndex: number
  ) => Promise<boolean>;
  orderBy: string | null;
  isOrderAscending: boolean;
  onSortColumn: (column: string) => void;
}

export function ProductTable({
  groups,
  isLoading,
  isWarehouseUser,
  onArchiveSku,
  onArchiveProduct,
  hasActiveFilters,
  onClearFilters,
  canManageSkus,
  onUploadClick,
  onAddClick,
  onAddPackSize,
  onCloneProduct,
  onCloneProductSkus,
  onCloneHierarchy,
  onSavePanelEdit,
  onRemovePallet,
  orderBy,
  isOrderAscending,
  onSortColumn,
}: ProductTableProps) {
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(
    new Set()
  );
  const [expandedPanelKeys, setExpandedPanelKeys] = useState<Set<string>>(
    new Set()
  );
  const [activeEdit, setActiveEdit] = useState<ActivePanelEdit | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const copyResetTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(copyResetTimeout.current), []);

  const cancelEdit = useCallback(() => setActiveEdit(null), []);

  useEffect(() => {
    if (!activeEdit) return undefined;
    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelEdit();
    };
    window.addEventListener('keydown', cancelOnEscape);
    return () => window.removeEventListener('keydown', cancelOnEscape);
  }, [activeEdit, cancelEdit]);

  const startEdit = (target: PanelEditTarget) => {
    const group = groups.find(
      (candidate) => productGroupKey(candidate) === target.groupKey
    );
    if (!group) return;
    if (target.kind === 'product') {
      setActiveEdit({ ...target, draft: productDraftOf(group) });
      return;
    }
    const sku = group.skus.find(
      (candidate) => candidate.inventorySkuId === target.inventorySkuId
    );
    if (!sku) return;
    if (target.kind === 'sku') {
      setExpandedPanelKeys((previous) =>
        new Set(previous).add(`sku-${sku.inventorySkuId}`)
      );
      setActiveEdit({ ...target, draft: skuDraftOf(sku) });
      return;
    }
    const pallet = (sku.palletising ?? [])[target.palletIndex];
    if (!pallet) return;
    setExpandedPanelKeys((previous) => {
      const next = new Set(previous);
      next.add(`sku-${sku.inventorySkuId}`);
      next.add(`pallet-${sku.inventorySkuId}-${target.palletIndex}`);
      return next;
    });
    setActiveEdit({ ...target, draft: palletDraftOf(pallet) });
  };

  const startAdditionalField = (groupKey: string, sku: InventorySKUListing) => {
    setExpandedPanelKeys((previous) =>
      new Set(previous).add(`sku-${sku.inventorySkuId}`)
    );
    setActiveEdit({
      kind: 'sku',
      groupKey,
      inventorySkuId: sku.inventorySkuId,
      draft: withAnEmptyAdditionalField(skuDraftOf(sku)),
    });
  };

  const startPalletDraft = (groupKey: string, sku: InventorySKUListing) => {
    setExpandedPanelKeys((previous) =>
      new Set(previous).add(`sku-${sku.inventorySkuId}`)
    );
    setActiveEdit({
      kind: 'pallet',
      groupKey,
      inventorySkuId: sku.inventorySkuId,
      palletIndex: sku.palletising?.length ?? 0,
      draft: emptyPalletDraft(),
    });
  };

  const changeDraft = (
    draft: ProductPanelDraft | SkuPanelDraft | PalletPanelDraft
  ) =>
    setActiveEdit((previous) =>
      previous === null ? previous : ({ ...previous, draft } as ActivePanelEdit)
    );

  const saveEdit = async () => {
    if (!activeEdit || isSaving) return;
    setIsSaving(true);
    const hasSaved = await onSavePanelEdit(activeEdit);
    setIsSaving(false);
    if (hasSaved) setActiveEdit(null);
  };

  const removePallet = async (
    _groupKey: string,
    sku: InventorySKUListing,
    palletIndex: number
  ) => {
    setActiveEdit(null);
    await onRemovePallet(sku, palletIndex);
  };

  const togglePanel = (panelKey: string) =>
    setExpandedPanelKeys((previous) => {
      const next = new Set(previous);
      if (next.has(panelKey)) {
        next.delete(panelKey);
      } else {
        next.add(panelKey);
      }
      return next;
    });

  const visibleGroupKeys = useMemo(() => groups.map(productGroupKey), [groups]);

  useEffect(() => {
    const visibleGroups = new Set(visibleGroupKeys);
    setExpandedGroupKeys((previous) => {
      const next = new Set(
        [...previous].filter((key) => visibleGroups.has(key))
      );
      return next.size === previous.size ? previous : next;
    });
  }, [visibleGroupKeys]);

  const handleToggleGroupExpand = (groupKey: string) => {
    setExpandedGroupKeys((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) {
        next.delete(groupKey);
        if (activeEdit?.groupKey === groupKey) setActiveEdit(null);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleCopy = (key: string, value: string) => {
    navigator.clipboard?.writeText(value);
    setCopiedKey(key);
    clearTimeout(copyResetTimeout.current);
    copyResetTimeout.current = setTimeout(() => setCopiedKey(null), 1500);
  };

  const headerCell = 'px-4 py-3 text-xs font-medium text-gray-500';

  const sortArrow = (column: string) => {
    if (orderBy === column) {
      return isOrderAscending ? (
        <ChevronUpIcon className='inline h-4 w-4' />
      ) : (
        <ChevronDownIcon className='inline h-4 w-4' />
      );
    }
    return <ChevronUpDownIcon className='inline h-4 w-4 text-gray-300' />;
  };

  return (
    <div
      className={`overflow-x-auto rounded-lg border border-gray-200 bg-white ${
        activeEdit ? 'pb-[76px]' : ''
      }`}
    >
      <table className='w-full min-w-[860px] table-auto'>
        <thead className='bg-gray-50'>
          <tr>
            <th scope='col' className='w-10 px-2'>
              <span className='sr-only'>Expand</span>
            </th>
            <th
              scope='col'
              onClick={() => onSortColumn('Name')}
              className={`${headerCell} cursor-pointer text-left hover:text-gray-700`}
            >
              Product {sortArrow('Name')}
            </th>
            <th
              scope='col'
              onClick={() => onSortColumn('SkuId')}
              className={`${headerCell} cursor-pointer text-left hover:text-gray-700`}
            >
              Product ID {sortArrow('SkuId')}
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              SKUs
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              Stock
            </th>
            <th scope='col' className={`${headerCell} text-center`}>
              Status
            </th>
            <th scope='col' className={`${headerCell} text-right`}>
              Actions
            </th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-200'>
          {isLoading && (
            <tr>
              <td colSpan={7} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <FloxSpinner size='md' />
                  <span className='text-sm text-gray-500'>
                    Loading products...
                  </span>
                </div>
              </td>
            </tr>
          )}
          {!isLoading && groups.length === 0 && (
            <tr>
              <td colSpan={7} className='py-12 text-center'>
                <div className='flex flex-col items-center gap-3'>
                  <CubeIcon className='h-12 w-12 text-gray-300' />
                  <p className='text-sm font-medium text-gray-900'>
                    No products found
                  </p>
                  {hasActiveFilters ? (
                    <>
                      <p className='text-sm text-gray-500'>
                        No products match the current filters.
                      </p>
                      <Button
                        intent='tertiary'
                        size='sm'
                        label='Clear filters'
                        onClick={onClearFilters}
                      />
                    </>
                  ) : (
                    <>
                      <p className='text-sm text-gray-500'>
                        Start with your first product — its SKUs become SKUs.
                      </p>
                      {canManageSkus && (
                        <div className='flex items-center gap-2'>
                          <Button
                            intent='primary'
                            size='sm'
                            label='Add your first product'
                            onClick={onAddClick}
                          />
                          <Button
                            intent='tertiary'
                            size='sm'
                            label='or upload in bulk'
                            onClick={onUploadClick}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </td>
            </tr>
          )}
          {!isLoading &&
            groups.map((group) => (
              <ProductTableRow
                key={productGroupKey(group)}
                group={group}
                isExpanded={expandedGroupKeys.has(productGroupKey(group))}
                onToggleExpand={handleToggleGroupExpand}
                isWarehouseUser={isWarehouseUser}
                onArchiveSku={onArchiveSku}
                onArchiveProduct={onArchiveProduct}
                copiedKey={copiedKey}
                onCopy={handleCopy}
                onAddPackSize={onAddPackSize}
                onAddAdditionalField={startAdditionalField}
                onCloneProduct={onCloneProduct}
                onCloneProductSkus={onCloneProductSkus}
                onCloneHierarchy={onCloneHierarchy}
                activeEdit={
                  activeEdit?.groupKey === productGroupKey(group)
                    ? activeEdit
                    : null
                }
                onDraftChange={changeDraft}
                onStartEdit={startEdit}
                onAddPallet={startPalletDraft}
                onRemovePallet={removePallet}
                expandedPanelKeys={expandedPanelKeys}
                onTogglePanel={togglePanel}
              />
            ))}
        </tbody>
      </table>
      {activeEdit && (
        <PanelSaveStrip
          isSaving={isSaving}
          isSaveEnabled={isActiveEditSaveable(activeEdit)}
          onCancel={cancelEdit}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}
